import { useEffect, useMemo, useState } from "react";
import { BookOpen, Loader2, Palette, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ACCENT_OPTIONS, CALLOUT_ICONS, CALLOUT_PALETTES, CALLOUT_VARIANT_META,
  DEFAULT_CALLOUT_STYLES, DEFAULT_HEADING, HEADING_CASES, HEADING_COLOURS,
  HEADING_SIZES, HEADING_WEIGHTS, RADIUS_OPTIONS, calloutColours, calloutCss,
  setLiveCalloutConfig,
} from "@/lib/kbCalloutStyles";

const Pick = ({ label, options, value, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm">
    <span className="text-slate-600 dark:text-slate-300">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
      {Object.entries(options).map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
    </select>
  </label>
);

/** KB Settings → Callout styles. Global shape preset plus a swatch and icon per
 *  callout type; dark mode is derived from the same hue. */
export default function KbCalloutStylesCard() {
  const [cfg, setCfg] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/kb/callout-styles").then(({ data }) => {
    setCanEdit(!!data.can_edit);
    setCfg({
      shape: { ...DEFAULT_CALLOUT_STYLES.shape, ...(data.shape || {}) },
      variants: { ...DEFAULT_CALLOUT_STYLES.variants, ...(data.variants || {}) },
    });
  }).catch(() => toast.error("Couldn't load the callout styles"));
  useEffect(() => { load(); }, []);

  // Live preview: the same CSS the reader gets, scoped to this card.
  const previewCss = useMemo(() => (cfg
    ? calloutCss(cfg).replace(/(^|\n)\./g, "$1#kb-callout-preview .") : ""), [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/kb/callout-styles", cfg);
      setCfg({ shape: { ...DEFAULT_CALLOUT_STYLES.shape, ...data.shape },
        variants: { ...DEFAULT_CALLOUT_STYLES.variants, ...data.variants } });
      const el = document.getElementById("kb-callout-styles");
      if (el) el.textContent = calloutCss(data);
      setLiveCalloutConfig(data);
      toast.success("Callout styles saved — every article uses them now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  const setShape = (k, v) => setCfg((c) => ({ ...c, shape: { ...c.shape, [k]: v } }));
  const setVariant = (id, k, v) => setCfg((c) => ({
    ...c, variants: { ...c.variants, [id]: { ...c.variants[id], [k]: v } },
  }));
  const setHeading = (id, k, v) => setCfg((c) => ({
    ...c,
    variants: {
      ...c.variants,
      [id]: { ...c.variants[id],
        heading: { ...DEFAULT_HEADING, ...(c.variants[id].heading || {}), [k]: v } },
    },
  }));

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900"
      data-testid="kb-callout-styles-card">
      <style>{previewCss}</style>
      <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800 dark:text-slate-100">
        <Palette className="h-4 w-4" /> Callout styles
        <a href="/kb/a/house-style-callouts-and-blocks" target="_blank" rel="noreferrer"
          className="ml-auto inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
          data-testid="kb-callout-house-style-link">
          <BookOpen className="h-3.5 w-3.5" /> House style reference
        </a>
      </div>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Shape applies to every callout; colour, icon and heading are per type. Change anything here
        and every article follows at once — swatches are fixed so text always stays readable, and
        the dark-mode version is worked out from the same colour.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Pick label="Corners" options={RADIUS_OPTIONS} value={cfg.shape.radius}
          onChange={(v) => setShape("radius", v)} testid="kb-callout-radius" />
        <Pick label="Accent" options={ACCENT_OPTIONS} value={cfg.shape.accent}
          onChange={(v) => setShape("accent", v)} testid="kb-callout-accent" />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-300">Thickness</span>
          <input type="number" min="0" max="8" value={cfg.shape.accent_width}
            onChange={(e) => setShape("accent_width", e.target.value)}
            data-testid="kb-callout-width"
            className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
          <span className="text-xs text-slate-400">px</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-300">Icons</span>
          <Switch checked={cfg.shape.icons} onCheckedChange={(v) => setShape("icons", v)}
            data-testid="kb-callout-icons-toggle" />
        </label>
      </div>

      {canEdit && (
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400"
          data-testid="kb-callout-hint">
          Each type is set on its own below, and whatever you choose applies to every article that
          uses that callout — change Warning here and every Warning box in the KB follows.
        </div>
      )}

      <div id="kb-callout-preview" className="mt-5 grid gap-3 lg:grid-cols-2">
        {CALLOUT_VARIANT_META.map(([id, label]) => {
          const conf = cfg.variants[id];
          const head = { ...DEFAULT_HEADING, ...(conf.heading || {}) };
          const c = calloutColours(conf.palette);
          return (
            <div key={id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"
              data-testid={`kb-callout-row-${id}`}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</span>
                <span className="ml-auto flex items-center gap-1">
                  {Object.keys(CALLOUT_PALETTES).map((p) => (
                    <button key={p} type="button" title={CALLOUT_PALETTES[p].label}
                      disabled={!canEdit} onClick={() => setVariant(id, "palette", p)}
                      data-testid={`kb-callout-swatch-${id}-${p}`}
                      className={`h-5 w-5 rounded-full border-2 transition-transform hover:scale-110 ${
                        conf.palette === p ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                      style={{ background: calloutColours(p).light.accent }} />
                  ))}
                </span>
                <select value={conf.icon} disabled={!canEdit}
                  onChange={(e) => setVariant(id, "icon", e.target.value)}
                  data-testid={`kb-callout-icon-${id}`}
                  className="rounded-md border border-slate-300 px-1.5 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
                  {Object.entries(CALLOUT_ICONS).map(([k, o]) => (
                    <option key={k} value={k}>{o.char ? `${o.char} ` : ""}{o.label}</option>
                  ))}
                </select>
              </div>
              {canEdit && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5 dark:bg-slate-800/50"
                  data-testid={`kb-callout-heading-${id}`}>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Heading</span>
                  <Switch checked={head.show !== false} data-testid={`kb-callout-heading-show-${id}`}
                    onCheckedChange={(v) => setHeading(id, "show", v)} />
                  <input value={conf.title_default || ""} maxLength={60}
                    onChange={(e) => setVariant(id, "title_default", e.target.value)}
                    data-testid={`kb-callout-heading-text-${id}`} placeholder={label}
                    title={`Wording a new ${label} callout starts with — leave blank to use “${label}”`}
                    className="w-28 rounded border border-slate-300 px-1.5 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
                  {[["size", HEADING_SIZES], ["weight", HEADING_WEIGHTS],
                    ["case", HEADING_CASES], ["colour", HEADING_COLOURS]].map(([key, opts]) => (
                    <select key={key} value={head[key]} disabled={head.show === false}
                      onChange={(e) => setHeading(id, key, e.target.value)}
                      data-testid={`kb-callout-heading-${key}-${id}`}
                      className="rounded border border-slate-300 px-1 py-0.5 text-[11px] disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
                      {Object.entries(opts).map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
                    </select>
                  ))}
                  <button type="button" onClick={() => setHeading(id, "show", head.show === false)}
                    data-testid={`kb-callout-heading-reset-${id}`}
                    className="ml-auto rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800">
                    {head.show === false ? "Show heading" : "Hide heading"}
                  </button>
                </div>
              )}
              <div className={`kb-callout kb-callout-${id}`} data-testid={`kb-callout-preview-${id}`}>
                <div className="kb-callout-title">{conf.title_default || label}</div>
                <div className="kb-callout-body">
                  <p>How a {label.toLowerCase()} box looks to readers · {CALLOUT_PALETTES[conf.palette].label}
                    <span className="ml-1 opacity-60">({c.light.accent})</span>
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="mt-4 flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid="kb-callout-save">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save callout styles
          </Button>
          <Button size="sm" variant="outline" data-testid="kb-callout-reset"
            onClick={() => setCfg({ shape: { ...DEFAULT_CALLOUT_STYLES.shape },
              variants: { ...DEFAULT_CALLOUT_STYLES.variants } })}>
            <RotateCcw className="mr-1 h-4 w-4" /> Back to defaults
          </Button>
        </div>
      )}
    </section>
  );
}
